import { useMemo, useState } from 'react';
import { App, Button, Empty, Pagination, Tag, Tooltip, type TableColumnsType } from 'antd';
import {
  CircleCheck,
  CirclePause,
  CircleAlert,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import ProTable from '@/components/pro/ProTable';
import { ApiError } from '@/api/errors';
import { formatExpire, formatTimestamp } from '@/utils/utils';
import { TokenStatus, TokenType, type AgentToken } from '@/proto/agentplatform/v2/token_pb';
import { type CreateAgentTokenData } from '@/proto/agentplatform/httpv2/token_pb';
import {
  CreateTokenDialog,
  TokenRevealDialog,
  useAgentTokens,
  useDeleteAgentToken,
  useRegenerateAgentToken,
  useToggleAgentToken,
} from '@/modules/tokens';

interface Props {
  accountId: string;
}

// 本页只服务 BRIDGE（自定义 WebSocket）渠道账号。后端 token 表 type 字段是 TokenType
// （API=1 / WEBSOCKET=2），业务语义等价：TokenType.WEBSOCKET ≡ ChannelType.BRIDGE。
const SCOPE_TOKEN_TYPE: TokenType = TokenType.WEBSOCKET;

/** Token tab：接真实 agentTokenApi，客户端按当前渠道账号过滤 + 客户端分页。
 *  新建复用 CreateTokenDialog（锁定当前账号）；明文复用 TokenRevealDialog。 */
export default function TokenTab({ accountId }: Props) {
  const { message, modal } = App.useApp();
  // 拉全量当前 TokenType 下的 token（后端 list 无 channel_account_id 过滤），本地再筛。
  const { data, isLoading } = useAgentTokens(SCOPE_TOKEN_TYPE, 1, 200);
  const toggleMut = useToggleAgentToken(SCOPE_TOKEN_TYPE);
  const regenMut = useRegenerateAgentToken(SCOPE_TOKEN_TYPE);
  const deleteMut = useDeleteAgentToken(SCOPE_TOKEN_TYPE);

  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<{ name: string; token: string } | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // list 无 channelAccountId 过滤参数，按当前账号客户端过滤（对齐参考页语义）。
  const tokens = useMemo(
    () => (data?.items ?? []).filter((t) => t.channelAccountId === accountId),
    [data, accountId]
  );
  const total = tokens.length;
  const pagedTokens = useMemo(
    () => tokens.slice((page - 1) * pageSize, page * pageSize),
    [tokens, page, pageSize]
  );

  async function onToggle(t: AgentToken) {
    try {
      await toggleMut.mutateAsync({ id: t.id, enable: t.status !== TokenStatus.ENABLED });
      message.success(t.status === TokenStatus.ENABLED ? 'Token 已停用' : 'Token 已启用');
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  function onRegen(t: AgentToken) {
    modal.confirm({
      title: '重置 Token',
      content: `重置「${t.name}」？之前的 Token 立即失效，无法恢复。`,
      okText: '重置',
      async onOk() {
        try {
          const res = await regenMut.mutateAsync(t.id);
          setRevealed({ name: res.name, token: res.token });
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : (e as Error).message);
        }
      },
    });
  }

  function onDelete(t: AgentToken) {
    if (t.status === TokenStatus.ENABLED) {
      message.error('请先停用再删除');
      return;
    }
    modal.confirm({
      title: '删除 Token',
      content: `确认删除「${t.name}」？`,
      okType: 'danger',
      async onOk() {
        try {
          await deleteMut.mutateAsync(t.id);
          message.success('Token 已删除');
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : (e as Error).message);
        }
      },
    });
  }

  const columns: TableColumnsType<AgentToken> = [
    { title: '凭证名称', dataIndex: 'name', key: 'name' },
    {
      title: '连接 Token',
      key: 'token',
      render: (_, r) => <code className="pp-mono">{r.token}</code>,
    },
    {
      title: '状态',
      key: 'status',
      width: 90,
      render: (_, r) => {
        const { color, text, icon } =
          r.status === TokenStatus.EXPIRED
            ? { color: 'error' as const, text: '已过期', icon: <CircleAlert size={12} /> }
            : r.status === TokenStatus.ENABLED
              ? { color: 'success' as const, text: '已启用', icon: <CircleCheck size={12} /> }
              : { color: 'default' as const, text: '已停用', icon: <CirclePause size={12} /> };
        return (
          <Tag
            style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
            color={color}
            icon={icon}
          >
            {text}
          </Tag>
        );
      },
    },
    { title: '有效期', key: 'expireAt', width: 170, render: (_, r) => formatExpire(r.expireAt) },
    {
      title: '最近连接',
      key: 'lastUsedAt',
      width: 170,
      render: (_, r) => formatTimestamp(r.lastUsedAt),
    },
    {
      title: '创建时间',
      key: 'createdAt',
      width: 170,
      render: (_, r) => formatTimestamp(r.createdAt),
    },
    {
      title: '操作',
      key: '__actions',
      width: 150,
      align: 'left',
      fixed: 'right',
      render: (_, r) => (
        <>
          <Tooltip
            title={
              r.status === TokenStatus.EXPIRED
                ? '已过期'
                : r.status === TokenStatus.ENABLED
                  ? '停用'
                  : '启用'
            }
          >
            <Button
              type="text"
              size="small"
              disabled={r.status === TokenStatus.EXPIRED}
              icon={r.status === TokenStatus.ENABLED ? <Pause size={13} /> : <Play size={13} />}
              onClick={() => onToggle(r)}
            />
          </Tooltip>
          <Tooltip title="重置">
            <Button
              type="text"
              size="small"
              icon={<RefreshCw size={13} />}
              onClick={() => onRegen(r)}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Button
              type="text"
              size="small"
              icon={<Trash2 size={13} />}
              onClick={() => onDelete(r)}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="tk-toolbar">
        <span className="muted">连接 Token 仅创建时展示一次明文，后续脱敏</span>
        <Button
          type="primary"
          size="small"
          icon={<Plus size={14} />}
          onClick={() => setCreateOpen(true)}
        >
          新建 Token
        </Button>
      </div>

      <ProTable<AgentToken>
        data={pagedTokens}
        loading={isLoading}
        columns={columns}
        rowKey="id"
        emptyChildren={
          <Empty description="还没有 Token">
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
              新建第一个
            </Button>
          </Empty>
        }
      />

      {!isLoading && total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            pageSizeOptions={[10, 20, 50, 100]}
            showTotal={(t) => `共 ${t} 条`}
            onChange={(p, ps) => {
              setPage(p);
              setPageSize(ps);
            }}
          />
        </div>
      )}

      <CreateTokenDialog
        open={createOpen}
        tokenType={TokenType.WEBSOCKET}
        lockedChannelAccountId={accountId}
        onClose={() => setCreateOpen(false)}
        onCreated={(d: CreateAgentTokenData) => setRevealed({ name: d.name, token: d.token })}
      />
      <TokenRevealDialog open={!!revealed} data={revealed} onClose={() => setRevealed(null)} />
    </>
  );
}
